import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Badge,
  Skeleton,
  Button,
} from '@vnetwork/ui/primitives';
import { DataTable, Stat, Pagination, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Receipt } from 'lucide-react';
import {
  useCredits,
  useInvoices,
  useCreditTransactions,
  isInvoicePaid,
  invoiceStatusTone,
  type InvoiceSummary,
  type CreditTransaction,
} from '@/api/billing';
import { formatCurrency } from '@/lib/format-currency';
import { formatDate } from '@/lib/format-date';
import { PaymentMethodsCard } from './PaymentMethodsCard';
import { CARD_PAYMENTS_ENABLED } from './card-payments-flag';
import { TopUpDialog } from './TopUpDialog';
import { brand } from '@/config/brand';
import styles from './Billing.module.css';

const PAGE_SIZE = 10;
type Filter = 'all' | 'unpaid' | 'paid';

export function BillingOverview() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>('all');
  const [txnPage, setTxnPage] = useState(1);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const { data: credits, isLoading: creditsLoading, refetch: refetchCredits } = useCredits();
  const { data: invoices, isLoading: invoicesLoading } = useInvoices(page, PAGE_SIZE);
  const { data: txns, isLoading: txnsLoading } = useCreditTransactions(txnPage, PAGE_SIZE);

  const balance = credits?.balance;

  const filteredInvoices = useMemo(() => {
    const rows = invoices?.invoices ?? [];
    if (filter === 'all') return rows;
    return rows.filter((i) =>
      filter === 'paid' ? isInvoicePaid(i.invoice_status) : !isInvoicePaid(i.invoice_status),
    );
  }, [invoices, filter]);

  const invoiceCols = useMemo<ColumnDef<InvoiceSummary, unknown>[]>(
    () => [
      {
        id: 'invoice',
        header: 'Invoice',
        cell: ({ row }) => (
          <span style={{ display: 'grid', gap: 1 }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>#{row.original.invoice_id}</span>
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              {row.original.invoice_description}
            </span>
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={invoiceStatusTone(row.original.invoice_status)}>
            {row.original.invoice_status}
          </StatusPill>
        ),
      },
      {
        id: 'total',
        header: 'Total',
        cell: ({ row }) => formatCurrency(row.original.invoice_total),
      },
      {
        id: 'due',
        header: 'Due',
        cell: ({ row }) => formatDate(row.original.invoice_duedate),
      },
      {
        id: 'created',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.invoice_created_at),
      },
    ],
    [],
  );

  const txnCols = useMemo<ColumnDef<CreditTransaction, unknown>[]>(
    () => [
      { id: 'date', header: 'Date', cell: ({ row }) => formatDate(row.original.created_at) },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const t = row.original.transaction_type;
          if (!t) return '—';
          // Type is the KIND of movement (kept neutral) — success/pending is shown
          // by the Status column, so a pending deposit never reads as completed.
          return (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.transaction_status;
          if (!s) return '—';
          const tone =
            s === 'completed' ? 'success' : s === 'failed' ? 'danger' : 'warning';
          return <StatusPill tone={tone}>{s}</StatusPill>;
        },
      },
      {
        id: 'desc',
        header: 'Description',
        cell: ({ row }) => row.original.note ?? '—',
      },
      {
        id: 'amount',
        header: 'Amount',
        cell: ({ row }) => (row.original.amount != null ? formatCurrency(row.original.amount) : '—'),
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      {brand.features.credit && (
        <div className={styles.statCards}>
          <Card flat>
            <CardBody>
              {creditsLoading ? (
                <Skeleton style={{ height: 56 }} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)' }}>
                  <Stat label="Remaining balance" value={formatCurrency(balance?.total_remaining ?? 0)} />
                  {brand.features.payments && (
                    <Button size="sm" onClick={() => setTopUpOpen(true)}>
                      Top up
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
          <Card flat>
            <CardBody>
              {creditsLoading ? (
                <Skeleton style={{ height: 56 }} />
              ) : (
                <Stat label="Total deposited" value={formatCurrency(balance?.total_deposited ?? 0)} />
              )}
            </CardBody>
          </Card>
          <Card flat>
            <CardBody>
              {creditsLoading ? (
                <Skeleton style={{ height: 56 }} />
              ) : (
                <Stat label="Total used" value={formatCurrency(balance?.total_used ?? 0)} />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Whole section stays hidden until Braintree card payments go live. */}
      {CARD_PAYMENTS_ENABLED && <PaymentMethodsCard />}

      <Card>
        <CardHeader
          actions={
            <div className={styles.filterRow}>
              {(['all', 'unpaid', 'paid'] as Filter[]).map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? 'primary' : 'secondary'}
                  onClick={() => setFilter(f)}
                >
                  {f[0].toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          }
        >
          <CardTitle>Invoices</CardTitle>
          <CardDescription>Your billing statements and their payment status.</CardDescription>
        </CardHeader>
        <CardBody>
          {invoicesLoading ? (
            <Skeleton style={{ height: 160 }} />
          ) : filteredInvoices.length === 0 ? (
            <EmptyState
              icon={<Receipt size={24} />}
              title={filter === 'all' ? 'No invoices yet' : `No ${filter} invoices`}
              description={`Invoices appear here as you use ${brand.shortName} services.`}
            />
          ) : (
            <>
              <DataTable
                columns={invoiceCols}
                data={filteredInvoices}
                getRowId={(r) => r.invoice_id}
                onRowClick={(r) => navigate(`invoices/${r.invoice_id}`)}
              />
              {invoices && invoices.total > PAGE_SIZE && filter === 'all' && (
                <div style={{ marginTop: 'var(--vn-space-4)' }}>
                  <Pagination
                    page={page}
                    pageCount={Math.ceil(invoices.total / PAGE_SIZE)}
                    onPageChange={setPage}
                    summary={`${invoices.total} invoices`}
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {brand.features.credit && (
        <Card>
          <CardHeader>
            <CardTitle>Credit transactions</CardTitle>
            <CardDescription>Deposits, usage and refunds on your account credit.</CardDescription>
          </CardHeader>
          <CardBody>
            {txnsLoading ? (
              <Skeleton style={{ height: 120 }} />
            ) : (txns?.transactions?.length ?? 0) === 0 ? (
              <EmptyState title="No transactions yet" description="Credit activity will show up here." />
            ) : (
              <>
                <DataTable
                  columns={txnCols}
                  data={txns?.transactions ?? []}
                  getRowId={(r, i) => String(r.transaction_id ?? i)}
                />
                {txns && txns.total > PAGE_SIZE && (
                  <div style={{ marginTop: 'var(--vn-space-4)' }}>
                    <Pagination
                      page={txnPage}
                      pageCount={Math.ceil(txns.total / PAGE_SIZE)}
                      onPageChange={setTxnPage}
                      summary={`${txns.total} transactions`}
                    />
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      )}

      <TopUpDialog
        open={topUpOpen}
        onOpenChange={setTopUpOpen}
        onClosed={() => refetchCredits()}
      />
    </div>
  );
}
